import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  linkedSignal,
  signal,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { IconComponent } from '../../presentation/icon/icon.component';
import { UiPopupDirective } from '../../../directives/ui/ui-popup.directive';
import {
  IMultiDateSettings,
  IUiOption,
  MultiDateSettings,
  UTILS,
} from '@aksia/infrastructure';
import { LabelComponent } from '../../presentation/label/label.component';
import {
  DATE_SELECTION,
  DateSettings,
  IDateSettings,
} from '@aksia/infrastructure';
import { ButtonComponent } from '../button/button.component';
import { DateComponent } from '../date/date.component';
import { ChipComponent } from '../../presentation/chip/chip.component';

@Component({
  selector: 'multi-date',
  imports: [
    CommonModule,
    LabelComponent,
    ButtonComponent,
    ChipComponent,
    IconComponent,
    UiPopupDirective,
    DatePipe,
  ],
  templateUrl: './multi-date.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiDateComponent extends DateComponent {
  //#region Inputs/Outputs

  override settings = input<MultiDateSettings, IMultiDateSettings>(
    new MultiDateSettings(),
    {
      transform: (settings: IMultiDateSettings) => this.initSettings(settings),
    },
  );

  //#endregion

  //#region Properties

  protected override valueFormatter(value: unknown) {
    return UTILS.OBJECT.isNil(value)
      ? ''
      : this.datePipe.transform(value as Date, this.format());
  }

  selectedDates = signal<Array<Date>>([]);

  public override options = computed<Array<IUiOption> | undefined>(() => {
    switch (this.calendarContent()) {
      case DATE_SELECTION.Day: {
        return [
          ...(this.settings()?.hideDayNames ? [] : this.dayOptionTitles),
          ...(this.settings()?.hideDayNames ? [] : this.daysOffset()),
          ...Array.from(
            Array(UTILS.DATE.getMonthDays(this.currentDate())),
            (_, i) => ({
              label: `${i + 1}`,
              value: i + 1,
              isSelected: this.selectedDates().some((d) =>
                UTILS.DATE.isSameDate(
                  d,
                  new Date(
                    this.currentDate().getFullYear(),
                    this.currentDate().getMonth(),
                    i + 1,
                  ),
                ),
              ),
              isSelectable: this.isDateSelectable(
                new Date(
                  this.currentDate().getFullYear(),
                  this.currentDate().getMonth(),
                  i + 1,
                ),
              ),
              isIndicated: this.isDateIndicated(
                new Date(
                  this.currentDate().getFullYear(),
                  this.currentDate().getMonth(),
                  i + 1,
                ),
              ),
            }),
          ),
        ];
      }
      case DATE_SELECTION.Month: {
        return Array.from(UTILS.CONSTANTS.MONTHS_TO_MMM, (month, i) => ({
          label: month,
          value: i,
          isSelected: this.selectedDates().some(
            (d) =>
              d.getMonth() === i &&
              d.getFullYear() === this.currentDate().getFullYear(),
          ),

          isSelectable: this.isDateSelectable(
            new Date(this.currentDate().getFullYear(), i, 1),
          ),
          isIndicated: this.isDateIndicated(
            new Date(this.currentDate().getFullYear(), i, 1),
          ),
        }));
      }
      case DATE_SELECTION.Year: {
        return this.years().map((year, i) => ({
          label: `${year}`,
          value: year,
          isSelected: this.selectedDates().some(
            (d) => d.getFullYear() === year,
          ),
          isSelectable: this.isDateSelectable(
            new Date(year, this.currentDate().getMonth(), 1),
          ),
          isIndicated: this.isDateIndicated(
            new Date(year, this.currentDate().getMonth(), 1),
          ),
        }));
      }
      default:
        return undefined;
    }
  });

  //#endregion

  //#region Functions

  constructor() {
    super();

    let valueTrackFx = effect(() => {
      this.selectedDates.set((this.value() as Array<Date>) || []);
      valueTrackFx.destroy();
    });

    this.stateComparer = UTILS.DATE.isSameDateCollection;
  }

  protected override initSettings(settings: IDateSettings) {
    let dateSettings = UTILS.OBJECT.assign(new DateSettings(), settings);
    dateSettings.defaultDate = settings.defaultDate;
    dateSettings.minDate = settings.minDate;
    dateSettings.maxDate = settings.maxDate;
    let dateFormat =
      settings.format ?? DateSettings.defaultFormat(settings.selection);
    dateSettings.placeholder = dateFormat;

    super.initSettings(dateSettings);
    this.selection.set(dateSettings.selection);
    this.format.set(dateFormat);
    this.yearsInPage.set(dateSettings.yearsInPage);

    if (dateSettings.defaultDate) {
      this.currentDate.set(dateSettings.defaultDate);
    }

    this.minDate.set(dateSettings.minDate);
    this.maxDate.set(dateSettings.maxDate);
    this.defaultIcon.set(dateSettings.defaultIcon);
    this.popupSettings.set(dateSettings.popup);
    this.popupSettings.update((popupSettings) => {
      popupSettings.uid = dateSettings.controlId;
      return popupSettings;
    });
    return dateSettings;
  }

  protected override select(
    option?: IUiOption,
    event: MouseEvent | null = null,
  ) {
    this.page.set(0);
    switch (this.calendarContent()) {
      case DATE_SELECTION.Day: {
        this.currentDate.update((currentDate: Date) => {
          return new Date(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            (option?.value as number) ?? 1,
          );
        });
        break;
      }
      case DATE_SELECTION.Month: {
        this.currentDate.update((currentDate: Date) => {
          return new Date(
            currentDate.getFullYear(),
            ((option?.value as number) ?? 0) + 1,
            0,
          );
        });
        break;
      }
      case DATE_SELECTION.Year: {
        this.currentDate.update((currentDate: Date) => {
          return new Date(
            (option?.value as number) ?? 1,
            currentDate.getMonth(),
            currentDate.getDate(),
          );
        });
        break;
      }
    }
    if (this.calendarContent() === this.selection()) {
      let newDateCollection = this.updateDateCollection(this.currentDate());
      super.updateValue(newDateCollection);
      this.selectedDates.set(newDateCollection);
    } else {
      this.calendarContent.set(
        this.calendarContent() === DATE_SELECTION.Year
          ? DATE_SELECTION.Month
          : DATE_SELECTION.Day,
      );
      //this.uiPopupDir.freeze();
    }
  }

  updateDateCollection(date: Date) {
    let newDateCollection = [...this.selectedDates()];
    if (!newDateCollection.find((d) => UTILS.DATE.isSameDate(d, date))) {
      newDateCollection.push(date);
      newDateCollection.sort((a, b) => a.getTime() - b.getTime());
    } else {
      newDateCollection = newDateCollection.filter(
        (d) => !UTILS.DATE.isSameDate(d, date),
      );
    }

    return newDateCollection;
  }

  //#endregion
}
